import { signal } from '@angular/core';
import { Svg } from '@svgdotjs/svg.js';

export type ViewBox = { x: number; y: number; width: number; height: number };

export function Zoom({ minZoom = 1, maxZoom = 10 }: { minZoom?: number; maxZoom?: number } = {}) {
  let givenSvg: Svg | undefined = undefined;
  let _originalViewBox: ViewBox | undefined = undefined;
  const currentZoom = signal(1);
  let _isDragging = false;
  let _lastMouseX = 0;
  let _lastMouseY = 0;

  function init(svg: Svg): void {
    if (!svg || !svg.node) return;

    _originalViewBox = svg.viewbox();

    givenSvg = svg;
    const svgElement = svg.node;
    svgElement.addEventListener('wheel', _onWheel, { passive: false });
    svgElement.addEventListener('mousedown', _onMouseDown);
    svgElement.addEventListener('mousemove', _onMouseMove);
    svgElement.addEventListener('mouseup', _onMouseUp);
    svgElement.addEventListener('mouseleave', _onMouseLeave);
    svgElement.addEventListener('dblclick', reset);
    svgElement.addEventListener('dragstart', (e) => e.preventDefault());
  }

  function cleanupListeners(): void {
    if (givenSvg && givenSvg.node) {
      const svgElement = givenSvg.node;
      svgElement.removeEventListener('wheel', _onWheel);
      svgElement.removeEventListener('mousedown', _onMouseDown);
      svgElement.removeEventListener('mousemove', _onMouseMove);
      svgElement.removeEventListener('mouseup', _onMouseUp);
      svgElement.removeEventListener('mouseleave', _onMouseLeave);
      svgElement.removeEventListener('dblclick', reset);
    }
  }

  function _onWheel(event: WheelEvent): void {
    event.preventDefault();

    if (!givenSvg || !_originalViewBox) return;

    const mousePos = _getSvgMousePosition(event);
    if (!mousePos) return;

    const isZoomingIn = event.deltaY < 0;
    const zoomSensitivity = 0.1;
    const zoomFactor = isZoomingIn ? 1 + zoomSensitivity : 1 - zoomSensitivity;
    const newZoom = Math.max(minZoom, Math.min(maxZoom, currentZoom() * zoomFactor));

    if (newZoom === currentZoom()) return;

    const currentViewBox = givenSvg.viewbox();
    const mouseRelX = (mousePos.svgX - currentViewBox.x) / currentViewBox.width;
    const mouseRelY = (mousePos.svgY - currentViewBox.y) / currentViewBox.height;
    const oldWidth = currentViewBox.width;
    const oldHeight = currentViewBox.height;
    const newWidth = oldWidth * (currentZoom() / newZoom);
    const newHeight = oldHeight * (currentZoom() / newZoom);

    const newX = mousePos.svgX - mouseRelX * newWidth;
    const newY = mousePos.svgY - mouseRelY * newHeight;

    givenSvg.viewbox(newX, newY, newWidth, newHeight);
    currentZoom.set(newZoom);

    if (currentZoom() >= minZoom) {
      givenSvg.node.style.cursor = 'move';
    } else {
      givenSvg.node.style.cursor = 'cursor-zoom-in';
    }
  }

  function _onMouseDown(event: MouseEvent): void {
    if (currentZoom() >= minZoom) {
      event.preventDefault();

      _isDragging = true;
      _lastMouseX = event.clientX;
      _lastMouseY = event.clientY;
    }
  }

  function _onMouseMove(event: MouseEvent): void {
    if (!_isDragging || !givenSvg || !_originalViewBox) return;

    const dx = event.clientX - _lastMouseX;
    const dy = event.clientY - _lastMouseY;

    if (dx === 0 && dy === 0) return;

    const currentViewBox = givenSvg.viewbox();
    const scale = currentViewBox.width / givenSvg.node.clientWidth;
    const deltaX = dx * scale;
    const deltaY = dy * scale;
    const newX = currentViewBox.x - deltaX;
    const newY = currentViewBox.y - deltaY;
    const constrainedX = _getMaxMoveX(newX, currentViewBox.width);
    const constrainedY = _getMaxMoveY(newY, currentViewBox.height);

    givenSvg.viewbox(constrainedX, constrainedY, currentViewBox.width, currentViewBox.height);

    _lastMouseX = event.clientX;
    _lastMouseY = event.clientY;
  }

  function _onMouseUp(): void {
    _isDragging = false;
  }

  function _onMouseLeave(): void {
    _isDragging = false;
  }

  function _getMaxMoveX(x: number, viewBoxWidth: number): number {
    if (!_originalViewBox) return x;

    if (currentZoom() < minZoom) {
      return x;
    }

    if (currentZoom() < 1) {
      const contentLeft = _originalViewBox.x;
      const contentRight = _originalViewBox.x + _originalViewBox.width;
      const contentWidth = _originalViewBox.width;
      const minVisibleWidth = contentWidth * 0.5;
      const maxX = contentRight - minVisibleWidth;
      const minX = contentLeft + minVisibleWidth - viewBoxWidth;

      return Math.max(minX, Math.min(maxX, x));
    }

    const centerX = _originalViewBox.x + _originalViewBox.width / 2;
    const maxPanDistance = Math.max(0, (_originalViewBox.width - viewBoxWidth) / 2);
    const minX = centerX - maxPanDistance - viewBoxWidth / 2;
    const maxX = centerX + maxPanDistance - viewBoxWidth / 2;

    return Math.max(minX, Math.min(maxX, x));
  }

  function _getMaxMoveY(y: number, viewBoxHeight: number): number {
    if (!_originalViewBox) return y;

    if (currentZoom() < minZoom) {
      return y;
    }

    if (currentZoom() < 1) {
      const contentTop = _originalViewBox.y;
      const contentBottom = _originalViewBox.y + _originalViewBox.height;
      const contentHeight = _originalViewBox.height;
      const minVisibleHeight = contentHeight * 0.5;
      const maxY = contentBottom - minVisibleHeight;
      const minY = contentTop + minVisibleHeight - viewBoxHeight;

      return Math.max(minY, Math.min(maxY, y));
    }

    const centerY = _originalViewBox.y + _originalViewBox.height / 2;
    const maxPanDistance = Math.max(0, (_originalViewBox.height - viewBoxHeight) / 2);
    const minY = centerY - maxPanDistance - viewBoxHeight / 2;
    const maxY = centerY + maxPanDistance - viewBoxHeight / 2;

    return Math.max(minY, Math.min(maxY, y));
  }

  function _getSvgMousePosition(event: MouseEvent): { svgX: number; svgY: number } | null {
    if (!givenSvg || !givenSvg.node) return null;

    const svgElement = givenSvg.node;
    const point = svgElement.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const ctm = svgElement.getScreenCTM();
    if (!ctm) return null;

    const svgPoint = point.matrixTransform(ctm.inverse());
    return {
      svgX: svgPoint.x,
      svgY: svgPoint.y,
    };
  }

  function reset() {
    if (givenSvg && _originalViewBox) {
      currentZoom.set(1);
      _isDragging = false;
      givenSvg.viewbox(_originalViewBox.x, _originalViewBox.y, _originalViewBox.width, _originalViewBox.height);
      if (givenSvg.node) {
        givenSvg.node.style.cursor = '';
      }
    }
  }

  return {
    init,
    reset,
    cleanupListeners,
    currentZoom: currentZoom.asReadonly(),
  };
}
